import { useCallback, useState } from 'react'
import { useCurrentUser } from '~/lib/auth/use-current-user'
import { useDashboardChannel } from '~/lib/socket/use-dashboard-channel'
import type {
  Holiday,
  HolidayCreatePayload,
  HolidayUpdatePayload,
} from '~/lib/api/types'
import { ApiHttpError } from '~/lib/api/client'
import { HolidayForm } from './holiday-form'
import { HolidayList } from './holiday-list'
import {
  useCreateHoliday,
  useDeleteHoliday,
  useHolidays,
  useInvalidateHolidays,
  useUpdateHoliday,
} from './use-holidays'
import styles from './holidays-page.module.scss'

type FormMode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; holiday: Holiday }

export function HolidaysPage() {
  const { data: me } = useCurrentUser()
  const isAdmin = me?.user.is_admin ?? false

  const holidays = useHolidays()
  const create = useCreateHoliday()
  const update = useUpdateHoliday()
  const remove = useDeleteHoliday()

  const invalidate = useInvalidateHolidays()
  const onSocketChange = useCallback(
    (payload: { kind: string }) => {
      if (payload.kind === 'holiday') invalidate()
    },
    [invalidate]
  )
  useDashboardChannel(me?.socket_token ?? null, onSocketChange)

  const [form, setForm] = useState<FormMode>({ kind: 'closed' })

  const closeForm = () => {
    setForm({ kind: 'closed' })
    create.reset()
    update.reset()
  }

  const submit = async (
    payload: HolidayCreatePayload | HolidayUpdatePayload
  ) => {
    if (form.kind === 'create') {
      await create.mutateAsync(payload as HolidayCreatePayload)
      closeForm()
    } else if (form.kind === 'edit') {
      await update.mutateAsync({ id: form.holiday.id, payload })
      closeForm()
    }
  }

  const handleDelete = async (h: Holiday) => {
    const confirmed = window.confirm(
      `Delete "${h.name}"? This cannot be undone.`
    )
    if (!confirmed) return
    try {
      await remove.mutateAsync(h.id)
    } catch {
      /* error surfaces via remove.error below */
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h1 className={styles.heading}>Holidays</h1>
          <p className={styles.subhead}>
            {holidays.data
              ? `${holidays.data.window.from} → ${holidays.data.window.to}`
              : 'Loading…'}
          </p>
        </div>
        {isAdmin && form.kind === 'closed' && (
          <button
            type="button"
            className={styles.addBtn}
            onClick={() => setForm({ kind: 'create' })}
          >
            + Add holiday
          </button>
        )}
      </div>

      {form.kind === 'create' && (
        <HolidayForm
          onSubmit={submit}
          onCancel={closeForm}
          pending={create.isPending}
          error={create.error}
        />
      )}

      {form.kind === 'edit' && (
        <HolidayForm
          initial={form.holiday}
          onSubmit={submit}
          onCancel={closeForm}
          pending={update.isPending}
          error={update.error}
        />
      )}

      {remove.error && (
        <p className={styles.error} role="alert">
          {formatDeleteError(remove.error)}
        </p>
      )}

      {holidays.isLoading && (
        <p className={styles.placeholder} aria-live="polite">
          Loading holidays…
        </p>
      )}

      {holidays.isError && (
        <p className={styles.placeholder} role="alert">
          Couldn&apos;t load holidays.{' '}
          <button
            type="button"
            className={styles.retry}
            onClick={() => holidays.refetch()}
          >
            Retry →
          </button>
        </p>
      )}

      {holidays.data && (
        <HolidayList
          holidays={holidays.data.holidays}
          isAdmin={isAdmin}
          onEdit={h => setForm({ kind: 'edit', holiday: h })}
          onDelete={handleDelete}
        />
      )}
    </div>
  )
}

function formatDeleteError(err: ApiHttpError): string {
  if (err.status === 409)
    return 'Holiday is synced from Google Calendar; manage it there.'
  const body = err.body as { error?: { message?: string } } | null
  return body?.error?.message ?? 'Could not delete the holiday.'
}
